// Shuffleboard service — dual-write pattern
// Writes: localStorage primary (sync), DynamoDB fire-and-forget
// Reads: getGame() sync from localStorage; fetchGame() async from DynamoDB with localStorage fallback
// Polling uses fetchGame() so all players see live updates.

import * as db from './db';

const SB_KEY = 'betbuds_shuffleboard';

function getStored() {
  const stored = localStorage.getItem(SB_KEY);
  if (stored) return JSON.parse(stored);
  localStorage.setItem(SB_KEY, JSON.stringify([]));
  return [];
}

function save(data) {
  localStorage.setItem(SB_KEY, JSON.stringify(data));
}

function saveLocal(game) {
  const games = getStored();
  const idx = games.findIndex((g) => g.activityId === game.activityId);
  if (idx >= 0) games[idx] = game;
  else games.push(game);
  save(games);
}

function removeLocal(activityId) {
  const games = getStored().filter((g) => g.activityId !== activityId);
  save(games);
}

// Fire-and-forget helper — always logs, never throws
function dbWrite(label, promise) {
  promise
    .then((result) => console.log(`[shuffleboard] DynamoDB ${label} succeeded:`, result?.shuffleboardId || result))
    .catch((err) => console.error(`[shuffleboard] DynamoDB ${label} FAILED:`, err));
}

// Normalize a DynamoDB ShuffleboardGames record to the shape we use in the app
function fromDb(record) {
  if (!record) return null;
  const parse = (v) => {
    if (v == null) return [];
    if (typeof v === 'string') {
      try { return JSON.parse(v); } catch { return []; }
    }
    return v;
  };
  return {
    id: record.shuffleboardId,
    activityId: record.activityId,
    masterEventId: record.masterEventId,
    createdBy: record.createdBy,
    players: parse(record.players),
    rounds: parse(record.rounds),
    currentRound: record.currentRound || 1,
    currentStarterId: record.currentStarterId || null,
    targetScore: record.targetScore || 21,
    status: record.status || 'setup',
    winnerId: record.winnerId || null,
    createdAt: record.createdAt,
  };
}

function toDbInput(game) {
  return {
    shuffleboardId: game.id,
    activityId: game.activityId,
    masterEventId: game.masterEventId,
    createdBy: game.createdBy,
    players: JSON.stringify(game.players),
    rounds: JSON.stringify(game.rounds),
    currentRound: game.currentRound,
    currentStarterId: game.currentStarterId,
    targetScore: game.targetScore,
    status: game.status,
    winnerId: game.winnerId,
  };
}

function toDbUpdates(updates) {
  const dbUpdates = {};
  if (updates.players !== undefined) dbUpdates.players = JSON.stringify(updates.players);
  if (updates.rounds !== undefined) dbUpdates.rounds = JSON.stringify(updates.rounds);
  if (updates.currentRound !== undefined) dbUpdates.currentRound = updates.currentRound;
  if (updates.currentStarterId !== undefined) dbUpdates.currentStarterId = updates.currentStarterId;
  if (updates.targetScore !== undefined) dbUpdates.targetScore = updates.targetScore;
  if (updates.status !== undefined) dbUpdates.status = updates.status;
  if (updates.winnerId !== undefined) dbUpdates.winnerId = updates.winnerId;
  return dbUpdates;
}

export const shuffleboardService = {
  /**
   * Synchronous read from localStorage only.
   * Use this when you need the current game state right now.
   */
  getGame(activityId) {
    return getStored().find((g) => g.activityId === activityId) || null;
  },

  /**
   * Async read — DynamoDB first, localStorage fallback.
   * Use this for polling so all players see live updates from other devices.
   * Also hydrates localStorage with the fresh DynamoDB state.
   */
  async fetchGame(activityId) {
    try {
      const record = await db.getShuffleboardGameByActivity(activityId);
      if (record) {
        const game = fromDb(record);
        saveLocal(game); // keep localStorage in sync
        return game;
      }
      // DynamoDB has no record — clear stale local state too
      removeLocal(activityId);
      return null;
    } catch (err) {
      console.warn('[shuffleboard] fetchGame DynamoDB failed, using localStorage:', err);
      return this.getGame(activityId);
    }
  },

  /**
   * Create a new game (setup phase).
   * Writes to localStorage first, fires DynamoDB write async.
   */
  createGame({ activityId, masterEventId, createdBy, players, targetScore = 21 }) {
    // Replace any existing game for this activity
    const existing = this.getGame(activityId);
    if (existing?.id) {
      dbWrite('deleteShuffleboardGame (replace)', db.deleteShuffleboardGame(existing.id));
    }

    const game = {
      id: `sb_${Date.now()}`,
      activityId,
      masterEventId,
      createdBy,
      players: players.map((p) => ({ userId: p.userId, displayName: p.displayName, score: 0 })),
      rounds: [],
      currentRound: 1,
      currentStarterId: players[0]?.userId || null,
      targetScore,
      status: 'setup',
      winnerId: null,
      createdAt: new Date().toISOString(),
    };

    // localStorage first (sync)
    saveLocal(game);

    // DynamoDB fire-and-forget
    console.log('[shuffleboard] Creating game in DynamoDB:', game.id);
    dbWrite('createShuffleboardGame', db.createShuffleboardGame(toDbInput(game)));

    return game;
  },

  /**
   * Start the game — moves from 'setup' to 'active'.
   */
  startGame(activityId) {
    const game = this.getGame(activityId);
    if (!game) throw new Error('Game not found');
    return this.updateGame(activityId, { status: 'active' });
  },

  /**
   * Submit a round: a player scored `points` points.
   * Any member of the activity can call this.
   */
  submitRound(activityId, { scoringUserId, points, enteredBy }) {
    const game = this.getGame(activityId);
    if (!game) throw new Error('Game not found');
    if (game.status !== 'active') throw new Error('Game is not active');
    if (!points || points <= 0) throw new Error('Points must be positive');

    // Apply points to the scoring player
    const updatedPlayers = game.players.map((p) =>
      p.userId === scoringUserId ? { ...p, score: (p.score || 0) + points } : p
    );

    const newRound = {
      roundNumber: game.currentRound,
      scoringUserId,
      points,
      enteredBy,
      enteredAt: new Date().toISOString(),
    };
    const updatedRounds = [...game.rounds, newRound];

    // Determine next starter: the non-scoring player with the lowest score
    const nonScoring = updatedPlayers.filter((p) => p.userId !== scoringUserId);
    const nextStarter = nonScoring.length > 0
      ? nonScoring.sort((a, b) => (a.score || 0) - (b.score || 0))[0].userId
      : scoringUserId;

    // Check for winner
    const scorer = updatedPlayers.find((p) => p.userId === scoringUserId);
    const reachedTarget = scorer && scorer.score >= game.targetScore;

    const updates = {
      players: updatedPlayers,
      rounds: updatedRounds,
      currentRound: reachedTarget ? game.currentRound : game.currentRound + 1,
      currentStarterId: reachedTarget ? game.currentStarterId : nextStarter,
      status: reachedTarget ? 'finished' : 'active',
      winnerId: reachedTarget ? scoringUserId : null,
    };

    return this.updateGame(activityId, updates);
  },

  /**
   * Undo the last round (any member can do this in case of mistake).
   */
  undoLastRound(activityId) {
    const game = this.getGame(activityId);
    if (!game) throw new Error('Game not found');
    if (game.rounds.length === 0) throw new Error('No rounds to undo');

    const lastRound = game.rounds[game.rounds.length - 1];
    const updatedPlayers = game.players.map((p) =>
      p.userId === lastRound.scoringUserId
        ? { ...p, score: Math.max(0, (p.score || 0) - lastRound.points) }
        : p
    );
    const updatedRounds = game.rounds.slice(0, -1);

    // Recompute starter from the new last round (or first player if no rounds left)
    let nextStarter = game.players[0]?.userId || null;
    if (updatedRounds.length > 0) {
      const prev = updatedRounds[updatedRounds.length - 1];
      const nonScoring = updatedPlayers.filter((p) => p.userId !== prev.scoringUserId);
      nextStarter = nonScoring.length > 0
        ? nonScoring.sort((a, b) => (a.score || 0) - (b.score || 0))[0].userId
        : prev.scoringUserId;
    }

    return this.updateGame(activityId, {
      players: updatedPlayers,
      rounds: updatedRounds,
      currentRound: lastRound.roundNumber,
      currentStarterId: nextStarter,
      status: 'active',
      winnerId: null,
    });
  },

  /**
   * Generic update — localStorage first, DynamoDB fire-and-forget.
   */
  updateGame(activityId, updates) {
    const game = this.getGame(activityId);
    if (!game) throw new Error('Game not found');

    const merged = { ...game, ...updates };
    saveLocal(merged);

    const dbUpdates = toDbUpdates(updates);
    if (Object.keys(dbUpdates).length > 0) {
      console.log('[shuffleboard] Updating game in DynamoDB:', game.id, Object.keys(dbUpdates));
      dbWrite('updateShuffleboardGame', db.updateShuffleboardGame(game.id, dbUpdates));
    }
    return merged;
  },

  /**
   * Reset / delete the current game so a new one can be started.
   */
  resetGame(activityId) {
    const game = this.getGame(activityId);
    if (!game) return;
    removeLocal(activityId);
    console.log('[shuffleboard] Deleting game from DynamoDB:', game.id);
    dbWrite('deleteShuffleboardGame', db.deleteShuffleboardGame(game.id));
  },
};
